import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { Button, ThemedText, ConfirmDialog } from '../components';
import { organizationLicenseService, usageService, licensePlanService } from '../api';
import { extractItems } from '../utils/responseUtils';
import { ArrowLeft } from 'lucide-react';

import { FormGroup, FormLabel } from '../components/FormStyles';

// --- Helpers ---

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
};

const formatCurrency = (amount) => {
  if (amount == null) return '$0.00';
  return `$${Number(amount).toFixed(2)}`;
};

const getBarColor = (pct) => {
  if (pct > 95) return '#ef4444';
  if (pct > 80) return '#f97316';
  if (pct > 60) return '#f59e0b';
  return '#22c55e';
};

const STATUS_BADGE = {
  Active:      { color: '#22c55e', bg: '#22c55e20' },
  Trial:       { color: '#3b82f6', bg: '#3b82f620' },
  Expired:     { color: '#ef4444', bg: '#ef444420' },
  Suspended:   { color: '#f59e0b', bg: '#f59e0b20' },
  Cancelled:   { color: '#6b7280', bg: '#6b728020' },
  GracePeriod: { color: '#f97316', bg: '#f9731620' },
};

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const BackLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  font-size: 13px;
  font-weight: 500;
  margin-bottom: ${p => p.theme.spacing.md};
  &:hover { text-decoration: underline; }
`;

const PageHeader = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${p => p.bg || '#22c55e20'};
  color: ${p => p.color || '#22c55e'};
`;

const Card = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const CardTitle = styled.h3`
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 250px;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const DetailsSection = styled.div``;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.sm} 0;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const DetailValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const LimitRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.md};
  &:last-child { margin-bottom: 0; }
`;

const LimitLabel = styled.div`
  width: 130px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const ProgressBarOuter = styled.div`
  flex: 1;
  height: 10px;
  background-color: ${p => p.theme.colors.border};
  border-radius: 5px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  border-radius: 5px;
  width: ${p => Math.min(p.pct, 100)}%;
  background-color: ${p => p.barColor};
  transition: width 0.4s ease;
`;

const LimitValue = styled.div`
  width: 160px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.textColor || p.theme.colors.textSecondary};
`;

const UnlimitedLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const InfoCard = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
`;

const InfoValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.valueColor || p.theme.colors.text};
  margin-bottom: 4px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl} 0;
`;



const OverridesTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${p => p.theme.spacing.sm};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const OverrideItem = styled.div`
  padding: ${p => p.theme.spacing.sm};
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
`;

const OverrideLabel = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
  margin-bottom: 2px;
`;

const OverrideValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const PlanDefault = styled.span`
  font-size: 11px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
  font-weight: 400;
`;

// --- Modal Styled Components ---

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${p => p.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${p => p.theme.spacing.md};
`;

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${p => p.theme.shadows?.xl || '0 25px 50px rgba(0,0,0,0.25)'};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.cols || '1fr 1fr'};
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.sm};
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
    box-shadow: 0 0 0 3px ${p => (p.theme.colors.primary || p.theme.colors.tint) + '20'};
  }
`;

const ModalSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
    box-shadow: 0 0 0 3px ${p => (p.theme.colors.primary || p.theme.colors.tint) + '20'};
  }
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin-top: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.sm};
  padding-bottom: 6px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

// --- Component ---

const OrganizationLicenseDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { licenseId } = useParams();
  const { isSuperAdmin } = useRole();

  const [license, setLicense] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Confirm dialogs
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [licenseId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // First get license from all licenses list
      const allLicenses = await organizationLicenseService.getAllLicenses();
      const licensesList = extractItems(allLicenses);
      const licenseData = licensesList.find(l => l.id === Number(licenseId));

      if (!licenseData) {
        toast.error('License not found');
        navigate('/license-management');
        return;
      }

      setLicense(licenseData);

      // Now fetch usage data and plans in parallel
      const [usageRes, plansRes] = await Promise.all([
        usageService.getUsageSummary(licenseData.organizationId).catch(() => null),
        licensePlanService.getPlans(),
      ]);

      setUsage(usageRes?.data || usageRes);
      setPlans(extractItems(plansRes));
    } catch (err) {
      console.error('Failed to load license details:', err);
      toast.error('Failed to load license details');
    } finally {
      setLoading(false);
    }
  };

  // --- Action Handlers ---

  const handleSuspend = async () => {
    try {
      await organizationLicenseService.suspendLicense(license.id);
      toast.success('License suspended');
      setSuspendConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to suspend license');
    }
  };

  const handleReactivate = async () => {
    try {
      await organizationLicenseService.reactivateLicense(license.id);
      toast.success('License reactivated');
      setReactivateConfirmOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate license');
    }
  };

  const handleExtend = async () => {
    if (!extendDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      setSaving(true);
      await organizationLicenseService.extendLicense(license.id, extendDate);
      toast.success('License extended');
      setExtendModalOpen(false);
      setExtendDate('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to extend license');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a plan');
      return;
    }
    try {
      setSaving(true);
      await organizationLicenseService.updateLicense(license.id, {
        licensePlanId: Number(selectedPlanId)
      });
      toast.success('Plan changed successfully');
      setChangePlanModalOpen(false);
      setSelectedPlanId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change plan');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOverrides = async () => {
    try {
      setSaving(true);
      const payload = {};

      // Only include fields that have values (empty string means clear override)
      if (overrideForm.overrideMaxUsers !== undefined) {
        payload.overrideMaxUsers = overrideForm.overrideMaxUsers === '' ? null : Number(overrideForm.overrideMaxUsers);
      }
      if (overrideForm.overrideMaxFormSchemas !== undefined) {
        payload.overrideMaxFormSchemas = overrideForm.overrideMaxFormSchemas === '' ? null : Number(overrideForm.overrideMaxFormSchemas);
      }
      if (overrideForm.overrideMaxSubmissionsPerMonth !== undefined) {
        payload.overrideMaxSubmissionsPerMonth = overrideForm.overrideMaxSubmissionsPerMonth === '' ? null : Number(overrideForm.overrideMaxSubmissionsPerMonth);
      }
      if (overrideForm.overrideMaxStorageBytes !== undefined) {
        payload.overrideMaxStorageBytes = overrideForm.overrideMaxStorageBytes === '' ? null : Number(overrideForm.overrideMaxStorageBytes);
      }
      if (overrideForm.overrideMaxPdfTemplates !== undefined) {
        payload.overrideMaxPdfTemplates = overrideForm.overrideMaxPdfTemplates === '' ? null : Number(overrideForm.overrideMaxPdfTemplates);
      }

      await organizationLicenseService.updateLicense(license.id, payload);
      toast.success('Overrides updated');
      setOverridesModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update overrides');
    } finally {
      setSaving(false);
    }
  };

  const openExtendModal = () => {
    setExtendDate(license?.expiryDate?.slice(0, 10) || '');
    setExtendModalOpen(true);
  };

  const openChangePlanModal = () => {
    setSelectedPlanId(String(license?.licensePlanId || ''));
    setChangePlanModalOpen(true);
  };

  const openOverridesModal = () => {
    setOverrideForm({
      overrideMaxUsers: license?.overrideMaxUsers ?? '',
      overrideMaxFormSchemas: license?.overrideMaxFormSchemas ?? '',
      overrideMaxSubmissionsPerMonth: license?.overrideMaxSubmissionsPerMonth ?? '',
      overrideMaxStorageBytes: license?.overrideMaxStorageBytes ?? '',
      overrideMaxPdfTemplates: license?.overrideMaxPdfTemplates ?? '',
    });
    setOverridesModalOpen(true);
  };

  // --- Limit rendering ---

  const renderLimit = (label, used, limit, isStorage = false) => {
    const usedVal = used ?? 0;
    const isUnlimited = limit == null || limit === 0;

    if (isUnlimited) {
      const displayUsed = isStorage ? formatBytes(usedVal) : usedVal.toLocaleString();
      return (
        <LimitRow theme={theme} key={label}>
          <LimitLabel theme={theme}>{label}</LimitLabel>
          <div style={{ flex: 1 }}>
            <UnlimitedLabel theme={theme}>Unlimited</UnlimitedLabel>
          </div>
          <LimitValue theme={theme}>{displayUsed} used</LimitValue>
        </LimitRow>
      );
    }

    const pct = Math.round((usedVal / limit) * 100);
    const barColor = getBarColor(pct);
    const displayUsed = isStorage ? formatBytes(usedVal) : usedVal.toLocaleString();
    const displayLimit = isStorage ? formatBytes(limit) : limit.toLocaleString();

    return (
      <LimitRow theme={theme} key={label}>
        <LimitLabel theme={theme}>{label}</LimitLabel>
        <ProgressBarOuter theme={theme}>
          <ProgressBarInner pct={pct} barColor={barColor} />
        </ProgressBarOuter>
        <LimitValue theme={theme} textColor={barColor}>
          {displayUsed} of {displayLimit} ({pct}%)
        </LimitValue>
      </LimitRow>
    );
  };

  // --- Computed values ---

  const orgName = license?.organization?.name || license?.organizationName || `Organization #${license?.organizationId}`;
  const planName = license?.licensePlan?.name || license?.planName || plans.find(p => p.id === license?.licensePlanId)?.name || '--';
  const status = license?.status || 'Active';
  const statusStyle = STATUS_BADGE[status] || STATUS_BADGE.Active;

  const expiryDate = license?.expiryDate;
  const daysLeft = daysUntil(expiryDate);
  const expiryColor = daysLeft != null
    ? (daysLeft <= 7 ? '#ef4444' : daysLeft <= 30 ? '#f97316' : '#22c55e')
    : undefined;

  const overageCost = usage?.totalOverageCostThisMonth ?? 0;

  // Get plan defaults for comparison
  const currentPlan = plans.find(p => p.id === license?.licensePlanId);

  // Check if there are any active overrides
  const hasOverrides = license && (
    license.overrideMaxUsers != null ||
    license.overrideMaxFormSchemas != null ||
    license.overrideMaxSubmissionsPerMonth != null ||
    license.overrideMaxStorageBytes != null ||
    license.overrideMaxPdfTemplates != null
  );

  // --- Access guard ---
  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/license-management">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/license-management">
      <PageContainer theme={theme}>
        <BackLink theme={theme} onClick={() => navigate('/license-management')}>
          <ArrowLeft size={14} /> Back to License Management
        </BackLink>

        {loading ? (
          <LoadingState theme={theme}>
            <ThemedText>Loading license details...</ThemedText>
          </LoadingState>
        ) : !license ? (
          <Card theme={theme}>
            <ThemedText>License not found.</ThemedText>
          </Card>
        ) : (
          <>
            {/* Header */}
            <PageHeader theme={theme}>
              <HeaderRow theme={theme}>
                <ThemedText type="title">{orgName}</ThemedText>
                <StatusBadge bg={statusStyle.bg} color={statusStyle.color}>
                  {status}
                </StatusBadge>
              </HeaderRow>
              <ThemedText type="subtitle">
                Plan: {planName}
              </ThemedText>
            </PageHeader>

            {/* License Details + Quick Actions */}
            <TwoColumnGrid theme={theme}>
              <Card theme={theme}>
                <CardTitle theme={theme}>License Details</CardTitle>
                <DetailsSection>
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Start Date</DetailLabel>
                    <DetailValue theme={theme}>{fmtDate(license.startDate)}</DetailValue>
                  </DetailRow>
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Expiry Date</DetailLabel>
                    <DetailValue theme={theme} style={{ color: expiryColor }}>
                      {fmtDate(expiryDate)}
                      {daysLeft != null && (
                        <span style={{ fontWeight: 400, marginLeft: 8 }}>
                          ({daysLeft > 0 ? `${daysLeft} days remaining` : daysLeft === 0 ? 'Expires today' : `Expired ${Math.abs(daysLeft)} days ago`})
                        </span>
                      )}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow theme={theme}>
                    <DetailLabel theme={theme}>Duration (from plan)</DetailLabel>
                    <DetailValue theme={theme}>{currentPlan?.durationInDays || '--'} days</DetailValue>
                  </DetailRow>
                  {license.notes && (
                    <DetailRow theme={theme}>
                      <DetailLabel theme={theme}>Notes</DetailLabel>
                      <DetailValue theme={theme}>{license.notes}</DetailValue>
                    </DetailRow>
                  )}
                </DetailsSection>
              </Card>

              <Card theme={theme}>
                <CardTitle theme={theme}>Quick Actions</CardTitle>
                <ActionsSection theme={theme}>
                  {(status === 'Active' || status === 'Trial' || status === 'GracePeriod') && (
                    <Button variant="danger" size="sm" onClick={() => setSuspendConfirmOpen(true)}>
                      Suspend License
                    </Button>
                  )}
                  {status === 'Suspended' && (
                    <Button variant="primary" size="sm" onClick={() => setReactivateConfirmOpen(true)}>
                      Reactivate License
                    </Button>
                  )}
                  {status !== 'Cancelled' && (
                    <>
                      <Button variant="secondary" size="sm" onClick={openExtendModal}>
                        Extend Expiry
                      </Button>
                      <Button variant="secondary" size="sm" onClick={openChangePlanModal}>
                        Change Plan
                      </Button>
                      <Button variant="secondary" size="sm" onClick={openOverridesModal}>
                        Edit Overrides
                      </Button>
                    </>
                  )}
                </ActionsSection>
              </Card>
            </TwoColumnGrid>

            {/* Resource Usage */}
            {usage && (
              <Card theme={theme}>
                <CardTitle theme={theme}>Resource Usage</CardTitle>
                {renderLimit(
                  'Users',
                  usage.users?.current,
                  usage.users?.limit
                )}
                {renderLimit(
                  'Forms',
                  usage.formSchemas?.current,
                  usage.formSchemas?.limit
                )}
                {renderLimit(
                  'Submissions/Mo',
                  usage.submissionsThisMonth?.current,
                  usage.submissionsThisMonth?.limit
                )}
                {usage.totalSubmissions && usage.totalSubmissions.limit != null && renderLimit(
                  'Total Submissions',
                  usage.totalSubmissions?.current,
                  usage.totalSubmissions?.limit
                )}
                {renderLimit(
                  'Storage',
                  usage.storage?.currentBytes,
                  usage.storage?.limitBytes,
                  true
                )}
                {renderLimit(
                  'Report Templates',
                  usage.pdfTemplates?.current,
                  usage.pdfTemplates?.limit
                )}
              </Card>
            )}

            {/* Expiry & Overage summary cards */}
            <InfoGrid theme={theme}>
              <InfoCard theme={theme}>
                <InfoLabel theme={theme}>License Expiry</InfoLabel>
                <InfoValue theme={theme} valueColor={expiryColor}>
                  {expiryDate ? fmtDate(expiryDate) : 'No Expiry'}
                </InfoValue>
                {daysLeft != null && (
                  <InfoLabel theme={theme}>
                    {daysLeft > 0
                      ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
                      : daysLeft === 0
                        ? 'Expires today'
                        : `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`}
                  </InfoLabel>
                )}
              </InfoCard>

              <InfoCard theme={theme}>
                <InfoLabel theme={theme}>Overage Charges This Month</InfoLabel>
                <InfoValue theme={theme} valueColor={overageCost > 0 ? '#f97316' : undefined}>
                  {formatCurrency(overageCost)}
                </InfoValue>
                {overageCost > 0 && (
                  <InfoLabel theme={theme}>
                    Charges for usage above plan limits
                  </InfoLabel>
                )}
              </InfoCard>
            </InfoGrid>

            {/* Override Limits */}
            {hasOverrides && (
              <Card theme={theme}>
                <CardTitle theme={theme}>Override Limits</CardTitle>
                <OverridesTable theme={theme}>
                  {license.overrideMaxUsers != null && (
                    <OverrideItem theme={theme}>
                      <OverrideLabel theme={theme}>Max Users</OverrideLabel>
                      <OverrideValue theme={theme}>
                        {license.overrideMaxUsers}
                        {currentPlan?.maxUsers != null && (
                          <PlanDefault theme={theme}> (plan: {currentPlan.maxUsers})</PlanDefault>
                        )}
                      </OverrideValue>
                    </OverrideItem>
                  )}
                  {license.overrideMaxFormSchemas != null && (
                    <OverrideItem theme={theme}>
                      <OverrideLabel theme={theme}>Max Forms</OverrideLabel>
                      <OverrideValue theme={theme}>
                        {license.overrideMaxFormSchemas}
                        {currentPlan?.maxFormSchemas != null && (
                          <PlanDefault theme={theme}> (plan: {currentPlan.maxFormSchemas})</PlanDefault>
                        )}
                      </OverrideValue>
                    </OverrideItem>
                  )}
                  {license.overrideMaxSubmissionsPerMonth != null && (
                    <OverrideItem theme={theme}>
                      <OverrideLabel theme={theme}>Max Submissions/Mo</OverrideLabel>
                      <OverrideValue theme={theme}>
                        {license.overrideMaxSubmissionsPerMonth}
                        {currentPlan?.maxSubmissionsPerMonth != null && (
                          <PlanDefault theme={theme}> (plan: {currentPlan.maxSubmissionsPerMonth})</PlanDefault>
                        )}
                      </OverrideValue>
                    </OverrideItem>
                  )}
                  {license.overrideMaxStorageBytes != null && (
                    <OverrideItem theme={theme}>
                      <OverrideLabel theme={theme}>Max Storage</OverrideLabel>
                      <OverrideValue theme={theme}>
                        {formatBytes(license.overrideMaxStorageBytes)}
                        {currentPlan?.maxStorageBytes != null && (
                          <PlanDefault theme={theme}> (plan: {formatBytes(currentPlan.maxStorageBytes)})</PlanDefault>
                        )}
                      </OverrideValue>
                    </OverrideItem>
                  )}
                  {license.overrideMaxPdfTemplates != null && (
                    <OverrideItem theme={theme}>
                      <OverrideLabel theme={theme}>Max Report Templates</OverrideLabel>
                      <OverrideValue theme={theme}>
                        {license.overrideMaxPdfTemplates}
                        {currentPlan?.maxPdfTemplates != null && (
                          <PlanDefault theme={theme}> (plan: {currentPlan.maxPdfTemplates})</PlanDefault>
                        )}
                      </OverrideValue>
                    </OverrideItem>
                  )}
                </OverridesTable>
              </Card>
            )}
          </>
        )}

        {/* Extend Expiry Modal */}
        <Overlay theme={theme} isOpen={extendModalOpen}>
          <ModalContent theme={theme} style={{ maxWidth: 400 }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Extend Expiry</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setExtendModalOpen(false); setExtendDate(''); }}>X</Button>
            </ModalHeader>
            <FormGroup>
              <FormLabel theme={theme}>New Expiry Date</FormLabel>
              <StyledInput theme={theme} type="date" value={extendDate} onChange={e => setExtendDate(e.target.value)} />
            </FormGroup>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={() => { setExtendModalOpen(false); setExtendDate(''); }}>Cancel</Button>
              <Button onClick={handleExtend} disabled={!extendDate || saving}>{saving ? 'Extending...' : 'Extend'}</Button>
            </ModalFooter>
          </ModalContent>
        </Overlay>

        {/* Change Plan Modal */}
        <Overlay theme={theme} isOpen={changePlanModalOpen}>
          <ModalContent theme={theme} style={{ maxWidth: 400 }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Change Plan</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setChangePlanModalOpen(false); setSelectedPlanId(''); }}>X</Button>
            </ModalHeader>
            <FormGroup>
              <FormLabel theme={theme}>Select Plan</FormLabel>
              <ModalSelect theme={theme} value={selectedPlanId} onChange={e => setSelectedPlanId(e.target.value)}>
                <option value="">Select a plan</option>
                {plans.filter(p => p.isActive !== false).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </ModalSelect>
            </FormGroup>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={() => { setChangePlanModalOpen(false); setSelectedPlanId(''); }}>Cancel</Button>
              <Button onClick={handleChangePlan} disabled={!selectedPlanId || saving}>{saving ? 'Changing...' : 'Change Plan'}</Button>
            </ModalFooter>
          </ModalContent>
        </Overlay>

        {/* Edit Overrides Modal */}
        <Overlay theme={theme} isOpen={overridesModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Edit Override Limits</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => setOverridesModalOpen(false)}>X</Button>
            </ModalHeader>
            <ThemedText type="body" style={{ marginBottom: theme.spacing.md, color: theme.colors.textSecondary }}>
              Leave fields empty to use plan defaults. Set a value to override the plan limit for this organization.
            </ThemedText>
            <SectionLabel theme={theme}>Override Limits</SectionLabel>
            <FormRow theme={theme}>
              <FormGroup>
                <FormLabel theme={theme}>Max Users {currentPlan?.maxUsers != null && <PlanDefault theme={theme}>(plan: {currentPlan.maxUsers})</PlanDefault>}</FormLabel>
                <StyledInput
                  theme={theme}
                  type="number"
                  min="0"
                  placeholder="Plan default"
                  value={overrideForm.overrideMaxUsers ?? ''}
                  onChange={e => setOverrideForm(prev => ({ ...prev, overrideMaxUsers: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel theme={theme}>Max Forms {currentPlan?.maxFormSchemas != null && <PlanDefault theme={theme}>(plan: {currentPlan.maxFormSchemas})</PlanDefault>}</FormLabel>
                <StyledInput
                  theme={theme}
                  type="number"
                  min="0"
                  placeholder="Plan default"
                  value={overrideForm.overrideMaxFormSchemas ?? ''}
                  onChange={e => setOverrideForm(prev => ({ ...prev, overrideMaxFormSchemas: e.target.value }))}
                />
              </FormGroup>
            </FormRow>
            <FormRow theme={theme}>
              <FormGroup>
                <FormLabel theme={theme}>Max Submissions/Mo {currentPlan?.maxSubmissionsPerMonth != null && <PlanDefault theme={theme}>(plan: {currentPlan.maxSubmissionsPerMonth})</PlanDefault>}</FormLabel>
                <StyledInput
                  theme={theme}
                  type="number"
                  min="0"
                  placeholder="Plan default"
                  value={overrideForm.overrideMaxSubmissionsPerMonth ?? ''}
                  onChange={e => setOverrideForm(prev => ({ ...prev, overrideMaxSubmissionsPerMonth: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel theme={theme}>Max Report Templates {currentPlan?.maxPdfTemplates != null && <PlanDefault theme={theme}>(plan: {currentPlan.maxPdfTemplates})</PlanDefault>}</FormLabel>
                <StyledInput
                  theme={theme}
                  type="number"
                  min="0"
                  placeholder="Plan default"
                  value={overrideForm.overrideMaxPdfTemplates ?? ''}
                  onChange={e => setOverrideForm(prev => ({ ...prev, overrideMaxPdfTemplates: e.target.value }))}
                />
              </FormGroup>
            </FormRow>
            <FormRow theme={theme} cols="1fr">
              <FormGroup>
                <FormLabel theme={theme}>Max Storage (bytes) {currentPlan?.maxStorageBytes != null && <PlanDefault theme={theme}>(plan: {formatBytes(currentPlan.maxStorageBytes)})</PlanDefault>}</FormLabel>
                <StyledInput
                  theme={theme}
                  type="number"
                  min="0"
                  placeholder="Plan default"
                  value={overrideForm.overrideMaxStorageBytes ?? ''}
                  onChange={e => setOverrideForm(prev => ({ ...prev, overrideMaxStorageBytes: e.target.value }))}
                />
              </FormGroup>
            </FormRow>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={() => setOverridesModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateOverrides} disabled={saving}>{saving ? 'Saving...' : 'Save Overrides'}</Button>
            </ModalFooter>
          </ModalContent>
        </Overlay>

        {/* Confirm Dialogs */}
        <ConfirmDialog
          isOpen={suspendConfirmOpen}
          title="Suspend License"
          message="Are you sure you want to suspend this organization's license? They will lose access to licensed features."
          onConfirm={handleSuspend}
          onCancel={() => setSuspendConfirmOpen(false)}
        />
        <ConfirmDialog
          isOpen={reactivateConfirmOpen}
          title="Reactivate License"
          message="Are you sure you want to reactivate this organization's license?"
          onConfirm={handleReactivate}
          onCancel={() => setReactivateConfirmOpen(false)}
        />
      </PageContainer>
    </Layout>
  );
};

export default OrganizationLicenseDetailPage;
export { OrganizationLicenseDetailPage };
